import React from 'react';
import { useLocation, Redirect } from 'react-router-dom';
import { AdminContext, AuthContext } from 'contexts';
import { isAssumeOrgType, featureEnabled } from 'utils';
import { isUserLicensed } from '@bb/users';
import {
  adminsHomePage,
  agentsHomePage,
  answeringHomePage,
} from '../constants';

const answeringUserPath = [
  'answering',
  'appointments',
  'listing',
  'users',
  'assets',
  'notification',
  'notifications',
  'inbox',
  'profile',
];

/** Any time the URL changes, send it through these series of checks
 * to ensure whether we should redirect the user from a restricted page. */
const AuthRedirects: React.FC<{
  isRestrictedNonMemberUser: boolean;
  isMLSWithMissingFeatures: boolean;
}> = ({ isRestrictedNonMemberUser, isMLSWithMissingFeatures }) => {
  const { auth, hasFeature } = React.useContext(AuthContext);
  const { isAdminAccessRestricted } = React.useContext(AdminContext) || {};
  const { pathname, search } = useLocation();
  const useEnhancedAnswering = React.useMemo(
    () => auth?.org.type === 'answering' || auth?.org.type === 'mlsStaff',
    [auth],
  );

  if (!auth) return null;

  const [, firstPath, secondPath] = pathname.split('/');

  // Redirection for answering and mls staff users
  if (isAssumeOrgType(auth.org.type)) {
    if (auth.org.type === 'mlsStaff') {
      if (isMLSWithMissingFeatures) {
        return <Redirect to="/feature-missing" />;
      }
      // Don't redirect to MLS staff pages here. Instead redirect to the correct page
      // in AnsweringSearchPage
      if (firstPath === 'mls') {
        return null;
      }
    }

    if (
      (!answeringUserPath.includes(firstPath) &&
        !(firstPath === 'my_business' && secondPath === 'showing')) ||
      pathname.includes('/listing/list')
    ) {
      return (
        <Redirect
          to={useEnhancedAnswering ? '/answering/search' : answeringHomePage}
        />
      );
    }
    return null;
  }

  // Redirection for non-answering service staff away from the call centre pages
  if (firstPath === 'answering' || pathname === '/') {
    if (auth.role === 'AGENT' || isUserLicensed(auth))
      return <Redirect to={agentsHomePage} />;
    return <Redirect to={adminsHomePage} />;
  }

  if (isRestrictedNonMemberUser) {
    return <Redirect to={'/non_member_restricted'} />;
  }

  // Redirection for admins
  if (firstPath === 'my_business' && secondPath !== 'showing') {
    if (
      !isUserLicensed(auth) &&
      auth.roles[auth.role] >= auth.roles.BRANCH_ADMIN
    ) {
      return <Redirect to={`${adminsHomePage}${search}`} />;
    }
  }

  // Redirection for agents
  if (firstPath === 'admin') {
    if (auth.roles[auth.role] < auth.roles.TEAM_ADMIN) {
      return <Redirect to={agentsHomePage} />;
    }
    if (isAdminAccessRestricted) {
      return <Redirect to={adminsHomePage} />;
    }
  }

  if (firstPath === 'lockboxes') {
    if (!hasFeature('MY_BROKERAGE') && !auth.isLicensed)
      return <Redirect to="feature-missing" />;
  }

  if (featureEnabled('removeAngularFeature') && firstPath === 'announcement') {
    if (auth.roles[auth.role] < auth.roles.BRANCH_ADMIN) {
      return <Redirect to={agentsHomePage} />;
    }
  }

  if (
    firstPath === 'subscriptions_payments' ||
    firstPath === 'answering_service'
  ) {
    // If the feature flag is off, redirect away from the page entirely
    if (!featureEnabled('subscriptionHubFeature')) {
      if (auth.role === 'AGENT' || isUserLicensed(auth))
        return <Redirect to={agentsHomePage} />;
      return <Redirect to={adminsHomePage} />;
    }

    // Only users with GLOBAL_ADMIN or higher can access the /org view; redirect others to /user
    if (
      secondPath === 'org' &&
      auth.roles[auth.role] < auth.roles.GLOBAL_ADMIN
    ) {
      return <Redirect to="/subscriptions_payments/user" />;
    }
  }

  return null;
};

export default AuthRedirects;
